import {
  ModalCustomComponent,
  useWSModalCustom,
  WSModalCustomProps
} from "@wingspanhq/fe-component-library";
import qs from "qs";
import React, { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { OptionalIfEmpty } from "../../types/utils";

type UseModalFlowComponentProps = {
  onFinish: (status: "success" | "cancel") => void;
};

export type { UseModalFlowComponentProps };

export const useModalFlow = <
  Props extends WSModalCustomProps<void> & UseModalFlowComponentProps
>({
  flowName,
  Component,
  getProps,
  onFinish
}: {
  flowName: string;
  Component: React.FC<Props>;
  getProps(
    data: Record<string, string>
  ): Omit<Props, "onClose" | "onFinish"> | undefined;
  onFinish: UseModalFlowComponentProps["onFinish"];
}) => {
  const history = useHistory();
  const location = useLocation();
  const searchString = location.search;

  const getPropsRef = useRef(getProps);
  const onFinishRef = useRef(onFinish);

  type ModalProps = Omit<Props, keyof UseModalFlowComponentProps>;

  const WrapperComponent = useMemo(() => {
    const ModalFlowWrapper: React.FC<ModalProps> = wrapperProps => (
      <Component
        {...(wrapperProps as unknown as Props)}
        onFinish={status => {
          history.push({
            pathname: history.location.pathname,
            search: ""
          });
          onFinishRef.current?.(status);
        }}
      />
    );
    ModalFlowWrapper.displayName = "ModalFlowWrapper";

    return ModalFlowWrapper;
  }, [Component, history]);

  const modal = useWSModalCustom(
    WrapperComponent as ModalCustomComponent<ModalProps, void>
  );

  useEffect(() => {
    const searchParams = qs.parse(searchString, {
      ignoreQueryPrefix: true
    });

    if (!modal.isOpened && searchParams.flowName === flowName) {
      const props = getPropsRef.current(searchParams as Record<string, string>);
      modal.open(props as any);
    } else if (modal.isOpened && searchParams.flowName !== flowName) {
      modal.close();
    }
  }, [modal, flowName, searchString]);

  return {
    open: (...args: OptionalIfEmpty<Omit<Props, "onFinish" | "onClose">>) => {
      const [props] = args;
      const searchParams = qs.stringify(
        { flowName, ...props },
        { addQueryPrefix: true }
      );
      history.push({
        pathname: history.location.pathname,
        search: searchParams
      });
    }
  };
};
